import _ from "lodash";
import React, {
  useState,
  useRef,
  memo,
  useMemo,
  useCallback,
  useEffect,
  useImperativeHandle,
} from "react";

// Contexts
import { LessonPlanningAssistantProvider } from "./contexts/LessonPlanningAssistantContext";

// Components
import I18nHOC from "UIComponents/I18nHOC";
import AIChat from "UIComponents/AIChat";
import Loading from "UIComponents/Loading";
import SuggestionLibrary from "./components/SuggestionLibrary";
import Body from "./components/Body";
import Footer from "./components/Footer";

// Design system
import { colors } from "@toddle-edu/ds-theme";
import { Modal, Checkbox, Button } from "@toddle-edu/ds-web";
import { DeleteOutlined, PencilOutlined } from "@toddle-edu/ds-icons";

// Utils / Services / Constants
import {
  AI_TRANSLATION_LANGUAGES,
  ASSESSMENT_TOOLS_OBJ,
} from "@toddle-edu/common-utils";
import {
  hideIntercom,
  showIntercom,
  resetAIConversation,
  updateAIConversation,
  getVariablesForLocalisedPlannerTemplates,
} from "modules/Services";
import { updateSettings } from "modules/ConfigurationThunks";
import {
  isPromptEqual,
  getActionsList,
  getAIPromptFilters,
  getIsAIResponseLoading,
  getTextValueForBlock,
  getHTMLContentForBlock,
  getToolTypeFromContent,
  customContentParser,
} from "./modules/Utils";
import {
  LOADING,
  STREAMING,
  ASSESSMENT,
  DP_ASSESSMENT_RUBRIC,
  SINGLE_POINT_RUBRIC,
  SCORE_BASED_RUBRIC,
  STANDARD_BASED_RUBRIC,
  FETCHING_TRANSCRIPT,
} from "Constants/stringConstants";
import { INITIAL_STATE, DIALOGS_INFO } from "./modules/Constants";
import {
  changeDisciplinaryContentToMultiField,
  getFieldValue,
  getUnitPlanDetails,
} from "Assessments/modules/Utils";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";
import { htmlToText, onEnterOrSpaceKeyDown } from "Utils";
import { TEMP_ID } from "AppComponents/AIContextBar/modules/Constants";
import { copyToClipboardWithMimeType } from "Utils/CopyToClipboard";
import { getIsAIEditDeleteButtonEnabled } from "Utils/ACLStoreUtils";
import {
  getUnprocessedLinkAttachments,
  isSchoolisationEnabled,
} from "Utils/AIUtils";

// Third party libs.
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import withDerivedProps from "lib/withDerivedProps";
import classNames from "classnames";

// Modules
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getLocalisedTemplate } from "IBPlanner/modules/IBPlannerModule";
import {
  getSelectedCurriculumId,
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
  getUserId,
  getUserType,
  getOrganizationId,
} from "modules/CommonSelectors";
import {
  onSendQuery,
  createAIConversation,
  cancelRequest,
} from "./modules/LessonPlanningAssistantModule";
import { getAssessmentDetailsForAIEnhancer } from "Assessments/modules/AssessmentQueryEnhancers";
import { getPlannerTemplateFromCache } from "modules/CommonGraphqlHelpers";
import { setGlobalTrayEnabledState } from "Login/modules/LoginModule";
import { getContextFieldsWithValue } from "./components/Footer/modules/FooterModule";
import { getAssessmentObj } from "ClassRoom/modules/ClassRoomModule";
import { updateAIDataInRedux } from "Assessments/modules/AssessmentModule";

// GraphQL
import {
  getAIConversationsQuery,
  getAIPromptsQuery,
  getUnitPlanBasicDetailsQuery,
} from "./modules/LessonPlanningQueries";

// Scss
import classes from "./LessonPlanningAssistantV2.scss";
import { getSchoolisationInstructionsQuery } from "Platform/routes/AdminSettings/routes/SchoolisationSettings/modules/SchoolisationQueries";

const LessonPlanningAssistantV2 = memo(props => {
  const {
    t,
    setShowAssistantRightPane,
    messages,
    leId,
    isAITutor,
    gradeId,
    course,
    mode,
    customRef,
    showLoader,
    assessmentType,
    isData,
    isLoading,
    context,
    sourceFrom,
    portalType,
    isDraft,
    templateId,
    testId,
    conversationMetadata,
    curriculumType,
    middlePaneRef,
    creationMode,
    entityType,
    setContext,
    prompts,
    courseId,
    isInitialState,
    aiConversationId,
    unitPlanDetails,
    assessmentDetails,
    unitPlanTemplate: _unitPlanTemplate,
    assessmentTemplate,
    learningCourseId,
    refetchAssessments,
    conversationAttachments,
    showIntercom,
    hideIntercom,
    onSendQuery,
    createAIConversation,
    setGlobalTrayEnabledState,
    getContextFieldsWithValue,
    updateAIConversation,
    setToastMsg,
    resetAIConversation,
    updateSettings,
    aiConversation,
    getAssessmentObj,
    footerInnerContainerStyle,
    openModalityId,
    updateAIDataInRedux,
    handleIsGeneratingAIResponse,
  } = props;

  // ======== states ========

  const [currentAIState, setCurrentAIState] = useState("");
  const [currentDialogAction, setCurrentDialogueAction] = useState(
    INITIAL_STATE.DIALOG_ACTION
  );
  const [selectedAction, setSelectedAction] = useState({});
  const [sendButtonParams, setSendButtonParams] = useState({});
  const [aiVoiceModalLanguage, setAiVoiceModalLanguage] = useState("en");
  const [excludedFields, setExcludedFields] = useState([]);
  const [promptSuggestionModalInfo, setPromptSuggestionModalInfo] = useState(
    INITIAL_STATE.PROMPT_SUGGESTION_MODAL_INFO
  );

  const [selectedLearningTool, setSelectedLearningTool] = useState(null);

  // ======== refs ========

  const containerRef = useRef(null);
  const aiChatRef = useRef(null);

  // ======== side effects =======

  // hide intercom because it is coming above chat send button
  useEffect(() => {
    hideIntercom();
    setGlobalTrayEnabledState(false);
    return () => {
      showIntercom();
      setGlobalTrayEnabledState(true);
    };
  }, []);

  // send message on prompt selection
  useEffect(() => {
    if (!_.isEmpty(selectedAction) && _.isEmpty(selectedAction?.label)) {
      onSendMessage();
    }
  }, [selectedAction]);

  // create ai message after createAttachment mutation success
  useEffect(() => {
    if (
      _.get(sendButtonParams, "attachmentHasTempId") &&
      _.every(conversationAttachments, ({ id }) => !_.includes(id, TEMP_ID))
    ) {
      onSendMessage();
    }
  }, [conversationAttachments, sendButtonParams?.attachmentHasTempId]);

  // if user closes the AI chat while the response is still being generated
  // and then re-opens it again we need to set current AI state
  useEffect(() => {
    const lastMessageId = _.get(_.last(messages), "node.id");
    setCurrentAIState(prevState => {
      if (_.isEmpty(prevState) && _.includes(lastMessageId, "NEW")) {
        return STREAMING;
      } else if (!_.includes(lastMessageId, "NEW") && prevState === STREAMING) {
        return "";
      }

      return prevState;
    });
  }, [messages]);

  // create a new ai conversation for old assignments (where ai conversation does not exist)
  useEffect(() => {
    const shouldCreateConversation =
      leId && _.isEmpty(aiConversationId) && !_.isEmpty(assessmentDetails);

    if (shouldCreateConversation) {
      const _createAIConversation = async () => {
        setCurrentAIState(LOADING);
        await createAIConversation({
          input: {
            entityType: ASSESSMENT,
            entityId: leId,
          },
        });
        setCurrentAIState("");
      };

      _createAIConversation();
    }
  }, [mode, aiConversationId, leId, assessmentDetails, createAIConversation]);

  useEffect(() => {
    const ref = aiChatRef.current;

    if (openModalityId && ref) {
      ref?.setSelectedModalityId(openModalityId);
      updateAIDataInRedux({ openModalityId: null });
    }
  }, [openModalityId, aiChatRef]);

  useEffect(() => {
    const { unprocessedLinks } = getUnprocessedLinkAttachments(messages);

    if (!_.isEmpty(unprocessedLinks) && _.isEmpty(currentAIState)) {
      setCurrentAIState(FETCHING_TRANSCRIPT);
    }
  }, [messages, currentAIState]);

  useImperativeHandle(
    customRef,
    () => ({
      messages,
      isAIResponseLoading: getIsAIResponseLoading(currentAIState),
      setCurrentDialogueAction,
    }),
    [messages, currentAIState]
  );

  // ======== variables =======

  const { action: dialogAction, params: dialogParams = {} } =
    currentDialogAction || {};

  const initialValue = useMemo(() => {
    // only set initial value if there has been no prior conversation
    if (!_.isEmpty(messages)) return null;

    const prompt = _.get(assessmentDetails, "metaData.aiPrompt", "");
    return htmlToText(prompt);
  }, [_.get(assessmentDetails, "metaData.aiPrompt", ""), messages]);

  const fieldList = _.get(assessmentTemplate, "field_list", {});
  const stepList = _.get(assessmentTemplate, "step_list", {});

  const isWritingMessage = useMemo(
    () => getIsAIResponseLoading(currentAIState),
    [currentAIState]
  );

  const shouldShowClearChat = !isWritingMessage && !_.isEmpty(messages);

  const unitPlanTemplate = _.get(
    getLocalisedTemplate({
      template: { body: _.cloneDeep(_unitPlanTemplate) },
      t,
      templateType: "unitPlan",
    }),
    "body",
    {}
  );

  const contextFields = getContextFieldsWithValue({
    t,
    context,
    template: assessmentTemplate,
    selectedAction,
    assessmentTemplate,
    unitPlanTemplate,
    assessmentDetails,
    unitPlanDetails,
    curriculumProgramType: curriculumType,
    excludedFields,
    course,
    creationMode,
    sourceFrom,
    assessmentMode: mode,
    middlePaneRef,
    isDraft,
    conversationMetadata,
    prompts,
    isAITutor,
    templateId,
  });

  const typeObj = getAssessmentObj({
    curriculumType,
    assessmentValue: assessmentType,
    contentType: ASSESSMENT,
  });

  const { typeLabel, typeLabel2 } = typeObj || {};
  const assessmentTypeLabel = t(typeLabel2 || typeLabel);

  const placeholder = isAITutor
    ? _.isEmpty(messages)
      ? t("ai:start_by_defining_your_tutor_objective")
      : t("common:type_here")
    : t("classRoom:start_typing_to_get_suggestions");

  // ======== utils =========

  const updatePromptSuggestionModalInfo = params => {
    setPromptSuggestionModalInfo(prevState => {
      return {
        ...prevState,
        ...params,
      };
    });
  };

  const onClickStop = useCallback(() => {
    cancelRequest?.();
    setCurrentAIState("STOP");
  }, [cancelRequest]);

  const handleActions = async params => {
    const { key, node, updateContentValue, updateAttachmentValue } = params;

    const { content, id: messageId, attachments } = node || {};

    switch (key) {
      case "copy": {
        const parsedContent = customContentParser(content);

        const textContent = await getTextValueForBlock({
          content: parsedContent,
        });

        const htmlContent = getHTMLContentForBlock({ content: parsedContent });

        copyToClipboardWithMimeType(
          {
            text: textContent,
            html: htmlContent,
          },
          () => {
            setToastMsg({
              msg: "toastMsgs:copied_capital",
              type: "success",
            });
          }
        );
        break;
      }
      case "edit": {
        const editedMessage = _.find(
          messages,
          message => _.get(message, "node.id") === messageId
        );

        if (editedMessage) {
          // update the states
          updateContentValue(_.trim(content) || "");
          updateAttachmentValue(attachments || []);
        }
        break;
      }
    }
  };

  const _getActionsList = node => {
    const actions = getActionsList({
      message: node,
    });

    if (getIsAIEditDeleteButtonEnabled()) {
      actions.push(
        {
          key: "internalEdit",
          icon: <PencilOutlined />,
          tooltipLabel: "common:edit",
        },
        {
          key: "delete",
          icon: <DeleteOutlined />,
          tooltipLabel: "common:delete",
        }
      );
    }

    return actions;
  };

  // ======== handlers =======

  // send message on enter
  const onSendMessage = async data => {
    const { checkLoadingState = true, content } = sendButtonParams;
    const { promptFields = [], attachments, learningTool, assistantMetadata } =
      data || {};

    if (getIsAIResponseLoading(currentAIState) && checkLoadingState) return;

    if (!isAITutor) {
      const metaDataPromptFields = _.get(
        conversationMetadata,
        "promptFields",
        []
      );

      // if there is no promptFields metadata then update the metadata
      if (aiConversationId && _.isEmpty(metaDataPromptFields)) {
        updateAIConversation({
          input: {
            id: aiConversationId,
            metadata: {
              ...conversationMetadata,
              promptFields,
            },
          },
        });
      } else if (
        aiConversationId &&
        !_.isEqualWith(promptFields, metaDataPromptFields, isPromptEqual) // if promptFields are not equal to the metadata promptFields then update the metadata
      ) {
        updateAIConversation({
          input: {
            id: aiConversationId,
            metadata: { ...conversationMetadata, promptFields },
          },
        });
      }
    }

    await onSendQuery({
      t,
      query: content || data?.content,
      leId,
      aiConversationId,
      selectedAction,
      setSelectedAction,
      setCurrentAIState,
      gradeId,
      course,
      messages,
      language: aiVoiceModalLanguage,
      context: _.merge({}, context, data?.context),
      setContext,
      fieldList,
      unitPlanDetails,
      assessmentDetails,
      excludedFields,
      entityType,
      stepList,
      template: assessmentTemplate,
      promptFields,
      prompts,
      unitPlanTemplate,
      attachments, // message level attachments
      conversationAttachments,
      refetchAssessments,
      isAITutor,
      mode,
      learningTool,
      setSendButtonParams,
      sendButtonParams: _.merge({}, sendButtonParams, data?.sendButtonParams),
      learningCourseId,
      toolChoice: data?.toolChoice,
      toolNames: data?.toolNames,
      selectedLearningTool,
      setSelectedLearningTool,
      assistantMetadata,
      schoolInstructions: _.get(props, "schoolInstructions"),
    });
  };

  const openingMessageResourceId = _.get(
    _.find(assessmentDetails?.allFields, {
      uid: "openingMessageResource",
    }),
    "resolvedMinimalTree.attachmentGroups.0.attachments.0.id",
    null
  );

  const contextProps = {
    onSendMessage,
    selectedLearningTool,
    setSelectedLearningTool,
    openingMessageResourceId,
    assessmentId: leId,
  };

  const handleClickReset = () => {
    setCurrentDialogueAction({
      action: "CLEAR_CHAT",
      params: {},
    });
  };

  const handleClose = () => {
    setShowAssistantRightPane(false);
  };

  const onClickSuggestionLibrary = () => {
    updatePromptSuggestionModalInfo({
      showModal: true,
      params: {},
    });
  };

  const onDialogConfirm = async currentAction => {
    const { action, params = {} } = currentAction || currentDialogAction || {};

    switch (action) {
      case "CLEAR_CHAT":
      case "CLEAR_CHAT_AND_GENERATE": {
        // 1. show full screen loader
        setCurrentAIState(LOADING);

        // 2. reset the AI conversation
        await resetAIConversation({
          input: { id: aiConversationId },
        });

        // 3. update the selected language in the AI conversation and reset promptFields
        const selectedLanguage = _.find(AI_TRANSLATION_LANGUAGES, {
          code: aiVoiceModalLanguage,
        });

        if (selectedLanguage) {
          await updateAIConversation({
            input: {
              id: aiConversationId,
              metadata: {
                language: _.get(selectedLanguage, "label"),
                languageCode: aiVoiceModalLanguage,
                selectedDropdownLanguage: _.get(selectedLanguage, "id"),
                promptFields: [],
              },
            },
          });
        }

        // 4. reset states
        setCurrentAIState("");
        setExcludedFields([]);
        setContext({});

        if (action === "CLEAR_CHAT_AND_GENERATE") {
          const { isChecked = false, selectedPrompt } = params || {};

          if (isChecked) {
            updateSettings({
              settings: [
                {
                  name: "ShowResetAIChatDialog",
                  value: "true",
                },
              ],
              showToastMsg: false,
            });
          }

          if (selectedPrompt) setSelectedAction(selectedPrompt);
          updatePromptSuggestionModalInfo({ showModal: false });
        }

        break;
      }
      case "REPLACE_CONTENT": {
        // if "do not show this message again" checkbox is checked then
        // save the user setting so that in future this dialog will not be shown
        if (params?.isChecked) {
          updateSettings({
            settings: [
              {
                name: "ShowReplaceContentDialog",
                value: "true",
              },
            ],
            showToastMsg: false,
          });
        }

        handleActions(params);
        break;
      }

      default: {
        handleActions(params);
      }
    }

    setCurrentDialogueAction({ action: "" });
  };

  const onPromptSuggestionModalClose = async params => {
    if (params) await onDialogConfirm(params);
    updatePromptSuggestionModalInfo({ showModal: false });
  };

  const onPromptSelection = useCallback(prompt => {
    setSelectedAction(prompt);
  }, []);

  const onClickDoNotShowAgain = () => {
    const { isChecked } = dialogParams || {};
    setCurrentDialogueAction({
      action: dialogAction,
      params: {
        ...dialogParams,
        isChecked: !isChecked,
      },
    });
  };

  // ======== components =======

  const renderSuggestionLibrary = () => {
    return promptSuggestionModalInfo?.showModal ? (
      <SuggestionLibrary
        t={t}
        initialGroupType={_.get(promptSuggestionModalInfo, "params.id")}
        onPromptSuggestionModalClose={onPromptSuggestionModalClose}
        onPromptSelection={onPromptSelection}
        prompts={prompts}
        language={aiVoiceModalLanguage}
        isAIResponseLoading={getIsAIResponseLoading(currentAIState)}
        assessmentDetails={assessmentDetails}
        unitPlanDetails={unitPlanDetails}
        conversationMetadata={conversationMetadata}
        setCurrentDialogueAction={setCurrentDialogueAction}
        isInitialState={isInitialState}
        isAITutor={isAITutor}
        selectedAction={selectedAction}
        setSelectedAction={setSelectedAction}
        leId={leId}
        gradeId={gradeId}
        course={course}
        assessmentTemplate={assessmentTemplate}
        portalType={portalType}
        entityType={entityType}
        learningCourseId={learningCourseId}
        setSelectedLearningTool={setSelectedLearningTool}
      />
    ) : null;
  };

  const renderDialog = () => {
    return (
      <Modal
        isOpen={!_.isEmpty(dialogAction)}
        onClose={() => setCurrentDialogueAction({ action: "" })}
        width={DIALOGS_INFO?.[dialogAction]?.width || "440px"}
        height={"fit-content"}
        zIndex={1111}
        dsVersion={"2.0"}
      >
        <Modal.Header
          title={DIALOGS_INFO?.[dialogAction]?.title({
            t,
            ...dialogParams,
          })}
          isClosable={true}
        />

        <Modal.Body>
          <div className={classes.modalBody}>
            {DIALOGS_INFO?.[dialogAction]?.message({
              t,
              ...dialogParams,
            })}

            {DIALOGS_INFO?.[dialogAction]?.showDoNotShowThisAgain ? (
              <div
                role={"button"}
                tabIndex={0}
                className={classes.modalCheckboxContainer}
                onClick={onClickDoNotShowAgain}
                onKeyDown={e => onEnterOrSpaceKeyDown(e, onClickDoNotShowAgain)}
              >
                <Checkbox
                  isChecked={dialogParams?.isChecked}
                  testId={`${testId}-lessonPlanningAssistant-modalDoNotShowAgain`}
                />
                <span>{t("common:dont_show_this_message_again")}</span>
              </div>
            ) : null}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <footer className={classes.modalFooter}>
            <Button
              dsVersion={"2.0"}
              variant={"neutral"}
              type={"outlined"}
              size={"medium"}
              onClick={() => setCurrentDialogueAction({ action: "" })}
              testId={`${testId}-lessonPlanningAssistant-modalCancel`}
            >
              {DIALOGS_INFO?.[dialogAction]?.buttonOneText({
                t,
                ...dialogParams,
              })}
            </Button>

            <Button
              dsVersion={"2.0"}
              variant={"destructive"}
              type={"fill"}
              size={"medium"}
              onClick={() => onDialogConfirm()}
              testId={`${testId}-lessonPlanningAssistant-modalConfirm`}
            >
              {DIALOGS_INFO?.[dialogAction]?.buttonTwoText({
                t,
                ...dialogParams,
              })}
            </Button>
          </footer>
        </Modal.Footer>
      </Modal>
    );
  };

  // ======== classes =======

  const blockContentClass = node => {
    const { metadata } = node || {};
    const { fieldType: field } = metadata || {};

    const isStandardOrScoreRubric = [
      SCORE_BASED_RUBRIC,
      STANDARD_BASED_RUBRIC,
    ].includes(field);

    return {
      [classes.tableWithEmptyHeaders]: isAITutor,
      [classes.assessmentToolMarkdown]: !_.isEmpty(ASSESSMENT_TOOLS_OBJ[field]),
      [classes.singlePointRubricTable]: field === SINGLE_POINT_RUBRIC,
      [classes.standardRubricTable]: isStandardOrScoreRubric,
    };
  };

  const blockClass = node => {
    const { content, metadata } = node || {};
    const { language } = metadata || {};

    const assessmentToolTypes = getToolTypeFromContent({
      language: language || aiVoiceModalLanguage,
      content,
      metadata,
      assessmentDetails,
    });

    return {
      [classes.dpAssessmentRubric]:
        _.first(assessmentToolTypes) === DP_ASSESSMENT_RUBRIC,
    };
  };

  const isDataLoading =
    showLoader || (!isData && isLoading) || currentAIState === LOADING;

  const containerClass = classNames({
    [classes.container]: true,
    [classes.tutorContainer]: isAITutor,
    [classes.containerBorderLeft]: !isAITutor, // this component is rendered on the left side for Ai tutor hence border left not required
  });

  return (
    <div className={containerClass} ref={containerRef}>
      {isDataLoading ? (
        <Loading />
      ) : (
        <LessonPlanningAssistantProvider>
          <AIChat
            mode={mode}
            conversation={aiConversation}
            ref={aiChatRef}
            entityType={ASSESSMENT}
            entitySubType={entityType}
            shouldDisplayModalities={isAITutor}
            contextProps={contextProps}
            currentAIState={currentAIState}
            setCurrentAIState={setCurrentAIState}
            source="LESSON_PLANNING_ASSISTANT"
          >
            {!isAITutor ? (
              <AIChat.Header
                onClose={setShowAssistantRightPane ? handleClose : null}
                onClickReset={shouldShowClearChat ? handleClickReset : null}
              />
            ) : null}
            <Body
              mode={mode}
              course={course}
              entityId={leId}
              messages={messages}
              prompts={prompts}
              gradeId={gradeId}
              courseId={courseId}
              sourceFrom={sourceFrom}
              portalType={portalType}
              context={context}
              isAITutor={isAITutor}
              setContext={setContext}
              excludedFields={excludedFields}
              middlePaneRef={middlePaneRef}
              initialValue={initialValue}
              onClickAction={handleActions}
              customAIActionsList={_getActionsList}
              currentAIState={currentAIState}
              assessmentDetails={assessmentDetails}
              refetchAssessments={refetchAssessments}
              assessmentTemplate={assessmentTemplate}
              setSelectedAction={setSelectedAction}
              setExcludedFields={setExcludedFields}
              assessmentTypeLabel={assessmentTypeLabel}
              onChangeSelectedPrompt={onPromptSelection}
              blockContentCustomClasses={blockContentClass}
              blockCustomClasses={blockClass}
              onClickSuggestionLibrary={onClickSuggestionLibrary}
              updatePromptSuggestionModalInfo={updatePromptSuggestionModalInfo}
              onSendMessage={onSendMessage}
              selectedLearningTool={selectedLearningTool}
              setSelectedLearningTool={setSelectedLearningTool}
            />
            <Footer
              mode={mode}
              placeholder={placeholder}
              onClickStop={onClickStop}
              contextFields={contextFields}
              onSendMessage={onSendMessage}
              selectedAction={selectedAction}
              setSelectedAction={setSelectedAction}
              isWritingMessage={isWritingMessage}
              onClickFooterOption={onClickSuggestionLibrary}
              conversationMetadata={conversationMetadata}
              isAITutor={isAITutor}
              aiConversationId={aiConversationId}
              initialValue={initialValue}
              unitPlanTemplate={unitPlanTemplate}
              unitPlanDetails={unitPlanDetails}
              portalType={portalType}
              assessmentTemplate={assessmentTemplate}
              assessmentDetails={assessmentDetails}
              context={context}
              entityId={leId}
              setContext={setContext}
              middlePaneRef={middlePaneRef}
              sourceFrom={sourceFrom}
              course={course}
              gradeId={gradeId}
              excludedFields={excludedFields}
              setExcludedFields={setExcludedFields}
              templateId={templateId}
              learningCourseId={learningCourseId}
              footerInnerContainerStyle={footerInnerContainerStyle}
              prompts={prompts}
              handleIsGeneratingAIResponse={handleIsGeneratingAIResponse}
            />
          </AIChat>
        </LessonPlanningAssistantProvider>
      )}

      {renderDialog()}

      {renderSuggestionLibrary()}
    </div>
  );
});

LessonPlanningAssistantV2.displayName = "LessonPlanningAssistantV2";

LessonPlanningAssistantV2.defaultProps = {
  portalType: "PLANNER",
};

const mapStateToProps = state => {
  const plannerElementSets = _.get(
    state,
    "platform.currentPlannerElementSets",
    []
  );

  const openModalityId = _.get(state, "assessment.aiData.openModalityId", null);

  const academicYearId = getSelectedAcademicYearId(state);
  const curriculumProgramId = getSelectedCurriculumId(state);
  const curriculumType = getCurrentCurriculumProgramType(state);
  const userId = getUserId(state);
  const userType = getUserType(state);
  const currentSelectedCourse = _.get(
    state,
    "teacher.selected_class.selected_course"
  );

  return {
    userId,
    userType,
    plannerElementSets,
    curriculumType,
    currentSelectedCourse,
    academicYearId,
    curriculumProgramId,
    openModalityId,
    organizationId: getOrganizationId(state),
  };
};

const getDerivedProps = ownProps => {
  const { isAITutor, currentSelectedCourse, academicYearId } = ownProps;

  const courseId =
    _.get(ownProps, "courseId") ||
    _.get(ownProps, "selectedCourse.courseId") ||
    currentSelectedCourse;

  const courseGradeFilters = {
    academicYearId,
  };

  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  return {
    containerStyle: {
      minWidth: isAITutor ? "480px" : "610px", // below 480px, AIContextBar overflows
      backgroundColor: colors.surfaceWhite,
      borderLeft: `1px solid ${colors.borderSubtle}`,
    },
    courseId,
    isData: true,
    isLoading: false,
    showLoaderBar: false,
    courseGradeFilters,
    collaboratorsFilter,
  };
};

const mapActionCreators = {
  showIntercom,
  hideIntercom,
  setToastMsg,
  resetAIConversation,
  onSendQuery,
  updateSettings,
  getAssessmentObj,
  createAIConversation,
  updateAIConversation,
  setGlobalTrayEnabledState,
  getContextFieldsWithValue,
  updateAIDataInRedux,
};

export default compose(
  I18nHOC({ resource: ["ai", "common"] }),
  connect(mapStateToProps, mapActionCreators),
  withDerivedProps(getDerivedProps),
  // fetches assessment details data for context
  getAssessmentDetailsForAIEnhancer,
  // fetches unit plan data from context state when learning course is enabled
  graphql(getUnitPlanBasicDetailsQuery, {
    name: "getUnitPlanBasicDetails",
    skip: ({ context }) =>
      !getIsLearningCoursesEnabled() || _.isEmpty(context?.unitPlan?.id),
    options: ({ context, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: context?.unitPlan?.id, portalType },
    }),
    props({
      getUnitPlanBasicDetails,
      ownProps: {
        t,
        i18n,
        curriculumType,
        assessmentTemplate,
        isData,
        isLoading,
      },
    }) {
      const { node = {}, networkStatus } = getUnitPlanBasicDetails;

      const unitPlanTemplateId = _.get(node, "templateId", null);

      const _unitPlanDetails = {
        unitPlanId: _.get(node, "id"),
        grades: _.get(node, "grades", []),
        unitPlanFields: _.get(node, "allFields", []),
        unitPlanResolvedMinimalTree: node || {},
      };

      const _unitPlanTemplate = getPlannerTemplateFromCache(
        getVariablesForLocalisedPlannerTemplates({
          templates: [unitPlanTemplateId],
          language: i18n.language,
        })
      );

      const resolvedSubjectNodes = _.get(
        _.find(_unitPlanDetails.unitPlanFields, { uid: "subjects" }),
        "resolvedMinimalTree.subjects",
        []
      );

      // modified unit plan template
      const unitPlanTemplate = _.get(
        changeDisciplinaryContentToMultiField({
          t,
          template: _unitPlanTemplate,
          unitType: getFieldValue({
            fields: _unitPlanDetails?.unitPlanFields,
            uid: "unitType",
          }),
          curriculumType,
          resolvedSubjectNodes,
        }),
        "body",
        {}
      );

      // modified unit plan details
      const unitPlanDetails = getUnitPlanDetails({
        unitPlanDetails: _unitPlanDetails,
        assessmentTemplate,
      });

      return {
        unitPlanDetails,
        unitPlanTemplate,
        isData: !_.isEmpty(node) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),

  // fetches conversation messages
  graphql(getAIConversationsQuery, {
    name: "getAIConversations",
    skip: ({ aiConversationId }) => _.isEmpty(aiConversationId),
    options: ({ aiConversationId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: aiConversationId, portalType },
      context: {
        useHttpLink: true,
      },
    }),
    props({ getAIConversations, ownProps: { isData, isLoading } }) {
      const { networkStatus, node } = getAIConversations;

      const messages = [..._.get(node, "messages.edges", [])].reverse();

      const conversationMetadata = _.get(node, "metadata");
      const conversationAttachments = _.get(node, "attachments", []);

      return {
        messages,
        aiConversation: node,
        isInitialState: _.isEmpty(messages),
        conversationMetadata,
        conversationAttachments,
        isData: !_.isEmpty(node) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  // fetches subject-specific prompts
  graphql(getAIPromptsQuery, {
    name: "getSubjectSpecificPrompts",
    skip: ({ leId, entityType }) => !leId || !entityType,
    options: ({
      leId,
      entityType,
      curriculumProgramId,
      portalType,
      gradeId,
      assessmentDetails,
      unitPlanDetails,
      curriculumType,
      isAITutor,
    }) => {
      const promptFilters = getAIPromptFilters({
        assessmentDetails,
        unitPlanDetails,
        curriculumProgramType: curriculumType,
        isAITutor,
      });

      return {
        fetchPolicy: "cache-and-network",
        variables: {
          filters: {
            entityId: leId,
            entityType,
            curriculumProgramId,
            gradeId,
            ...promptFilters,
          },
          portalType,
        },
        context: { useHttpLink: true },
      };
    },
    props({ getSubjectSpecificPrompts, ownProps: { isData, isLoading } }) {
      const queryData = _.get(getSubjectSpecificPrompts, "ai", {});

      const prompts = _.get(queryData, "prompts", []);
      const networkStatus = _.get(getSubjectSpecificPrompts, "networkStatus");

      const isSubjectSpecificPromptQueryLoading = _.includes(
        [1, 2],
        networkStatus
      );

      return {
        prompts,
        isSubjectSpecificPromptQueryLoading,
        isData: !_.isEmpty(queryData) && isData,
        isLoading: isSubjectSpecificPromptQueryLoading || isLoading,
      };
    },
  }),
  // fetches generic prompts if subject specific prompts are empty
  graphql(getAIPromptsQuery, {
    name: "getGenericPrompts",
    skip: ({
      leId,
      entityType,
      prompts,
      isSubjectSpecificPromptQueryLoading,
    }) =>
      !leId ||
      !entityType ||
      !_.isEmpty(prompts) ||
      isSubjectSpecificPromptQueryLoading,
    options: ({
      leId,
      entityType,
      curriculumProgramId,
      portalType,
      gradeId,
    }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          filters: {
            entityId: leId,
            entityType,
            curriculumProgramId,
            gradeId,
          },
          portalType,
        },
        context: { useHttpLink: true },
      };
    },
    props({ getGenericPrompts }) {
      const queryData = _.get(getGenericPrompts, "ai", {});

      const prompts = _.get(queryData, "prompts", []);
      const networkStatus = _.get(getGenericPrompts, "networkStatus");

      const showLoader =
        _.isEmpty(queryData) && _.includes([1, 2], networkStatus);

      return {
        prompts,
        showLoader,
      };
    },
  }),
  graphql(getSchoolisationInstructionsQuery, {
    name: "getSchoolisationInstructions",
    skip: () => !isSchoolisationEnabled(),
    options: ({ isAITutor }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        module: isAITutor ? "tutor_ai" : "assignment_assistant_ai",
      },
    }),
    props({ getSchoolisationInstructions }) {
      const instructionsData = _.get(
        getSchoolisationInstructions,
        "ai.schoolisationInstructions",
        []
      );

      const schoolInstructionBlock = _.map(instructionsData, block =>
        _.get(block, "content", "")
      ).join("\n\n");

      return {
        schoolInstructions: schoolInstructionBlock || "",
      };
    },
  })
)(LessonPlanningAssistantV2);
